// Imports unchanged...
import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  MenuItem,
  Stack,
  Divider,
  Chip,
  Alert,
  Button,
  IconButton,
  Paper,
  FormControl,
  InputLabel,
  Select,
  Fade,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PreviewIcon from "@mui/icons-material/Visibility";
import MessageIcon from "@mui/icons-material/Message";
import CreateIcon from "@mui/icons-material/Create";
import LanguageIcon from "@mui/icons-material/Language";
import ImageIcon from "@mui/icons-material/Image";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import DescriptionIcon from "@mui/icons-material/Description";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { submitTemplateToFacebook, createUploadTemplateMedia } from "../api/axios";

const CreateTemplate = () => {
  const [templateName, setTemplateName] = useState("");
  const [language, setLanguage] = useState("en");
  const [templateType, setTemplateType] = useState("Marketing");
  const [headerType, setHeaderType] = useState("NONE");
  const [mediaFile, setMediaFile] = useState(null);
  const [headerText, setHeaderText] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [footerText, setFooterText] = useState("");
  const [buttonText, setButtonText] = useState("");
  const [buttons, setButtons] = useState([]);

  const [placeholders, setPlaceholders] = useState({ header: [], body: [] });
  const [variableTypes, setVariableTypes] = useState({});
  const [headerExamples, setHeaderExamples] = useState({});
  const [bodyExamples, setBodyExamples] = useState({});
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    const headerMatches = new Set();
    const bodyMatches = new Set();
    (headerText.match(/{{(\d+)}}/g) || []).forEach((m) =>
      headerMatches.add(m.slice(2, -2))
    );
    (bodyText.match(/{{(\d+)}}/g) || []).forEach((m) =>
      bodyMatches.add(m.slice(2, -2))
    );

    setPlaceholders({
      header: Array.from(headerMatches).sort((a, b) => +a - +b),
      body: Array.from(bodyMatches).sort((a, b) => +a - +b),
    });

    setVariableTypes((prev) => {
      const next = { ...prev };
      [...headerMatches, ...bodyMatches].forEach((id) => {
        if (!next[id]) next[id] = "Text";
      });
      return next;
    });

    setHeaderExamples((prev) => {
      const next = { ...prev };
      [...headerMatches].forEach((id) => {
        if (!next[id]) next[id] = "";
      });
      return next;
    });

    setBodyExamples((prev) => {
      const next = { ...prev };
      [...bodyMatches].forEach((id) => {
        if (!next[id]) next[id] = "";
      });
      return next;
    });
  }, [headerText, bodyText]);

  const handleTypeChange = (id, val) => {
    setVariableTypes((prev) => ({ ...prev, [id]: val }));
  };

  const handleExampleChange = (id, val, type = "body") => {
    if (type === "header") {
      setHeaderExamples((prev) => ({ ...prev, [id]: val }));
    } else {
      setBodyExamples((prev) => ({ ...prev, [id]: val }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMediaFile(file);
      toast.info(`Selected media: ${file.name}`);
    }
  };

  const addButton = () => {
    if (buttonText.trim()) {
      setButtons((prev) => [...prev, buttonText.trim()]);
      toast.success(`Button added: ${buttonText.trim()}`);
      setButtonText("");
    }
  };

  const removeButton = (text) => {
    setButtons((prev) => prev.filter((b) => b !== text));
  };

  const validateTemplate = () => {
    const issues = [];
    const nameRx = /^[a-z0-9_]+$/;

    if (!templateName) issues.push("Template name is required.");
    else if (!nameRx.test(templateName))
      issues.push("Name must be lowercase, alphanumeric, underscore only.");

    if (!bodyText) issues.push("Body text is required.");

    if (headerType === "TEXT") {
      const match = headerText.trim().match(/{{(\d+)}}/g) || [];
      if (match.length > 1 || (match[0] && match[0] !== "{{1}}")) {
        issues.push("Header text must only contain {{1}} if variable is used.");
      }
    }

    return issues;
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  const issues = validateTemplate();

  if (issues.length) {
    setErrors(issues);
    toast.error("Validation errors");
    return;
  }

  setErrors([]);
  const components = [];
  let mediaUrl = "";
  let headerComponent = null;

  // ✅ Handle MEDIA header upload first
  if (["IMAGE", "VIDEO", "DOCUMENT"].includes(headerType)) {
    if (!mediaFile) {
      toast.error("Media file is required for header.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", mediaFile);

      const uploadRes = await createUploadTemplateMedia(formData);
      mediaUrl = uploadRes?.data?.url;

      if (!mediaUrl) {
        toast.error("Media upload failed.");
        return;
      }

      headerComponent = {
        type: "HEADER",
        format: headerType.toUpperCase(),
        example: {
          header_handle: [mediaUrl],
        },
      };
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Media upload failed.");
      return;
    }
  }

  // ✅ TEXT header
  if (headerType === "TEXT") {
    const trimmedHeader = headerText.trim();
    const hasVariable = trimmedHeader.includes("{{1}}");
    const headerExample = (headerExamples["1"] || "").trim();

    headerComponent = {
      type: "HEADER",
      format: "TEXT",
      text: trimmedHeader,
    };

    if (hasVariable) {
      headerComponent.example = {
        header_text: [headerExample || "Example Header"],
      };
    }
  }

  // ✅ Push headerComponent if defined
  if (headerComponent) {
    components.push(headerComponent);
  }

  // ✅ BODY component
  const sortedBodyVars = Object.keys(bodyExamples).sort((a, b) => +a - +b);
  const bodyEx = sortedBodyVars.map(
    (id) => bodyExamples[id] || `Example ${id}`
  );

  components.push({
    type: "BODY",
    text: bodyText.trim(),
    example: { body_text: [bodyEx] },
  });

  // ✅ FOOTER component
  if (footerText.trim()) {
    components.push({
      type: "FOOTER",
      text: footerText.trim(),
    });
  }

  // ✅ BUTTONS
  if (buttons.length) {
    components.push({
      type: "BUTTONS",
      buttons: buttons.map((t) => ({
        type: "QUICK_REPLY",
        text: t.trim(),
      })),
    });
  }

  // ✅ Variables metadata
  const variables = {};
  Object.entries(variableTypes).forEach(([id, type]) => {
    variables[id] = type;
  });

  // ✅ Final Payload
  const payload = {
    name: templateName.trim(),
    category: templateType.toUpperCase(),
    language,
    components,
    variables,
  };
  console.log("Submitted Template JSON:", JSON.stringify(payload));

  try {
    const res = await submitTemplateToFacebook(payload);
    toast.success("Template submitted to Facebook!");
    console.log("Submitted Template JSON:", JSON.stringify(payload));
  } catch (err) {
    console.error("Error submitting template:", err?.response || err);
    toast.error("Failed to submit template to Facebook.");
  }
};


  return (
    <Grid container spacing={2} sx={{ mt: 0, px: 0 }}>
      {errors.map((err, idx) => (
        <Alert key={idx} severity="error" sx={{ mb: 1 }}>
          {err}
        </Alert>
      ))}

      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          {/* Left Form */}
          <Grid item xs={12} md={7} sx={{ width: 650 }}>
            <Stack spacing={2}>
              <TextField
                size="small"
                label="Template Name"
                value={templateName}
                onChange={(e) =>
                  setTemplateName(
                    e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "")
                  )
                }
                helperText="Lowercase, alphanumeric, underscore (_) only"
                fullWidth
              />
              <FormControl size="small" fullWidth>
                <TextField
                  size="small"
                  select
                  label={
                    <Box display="flex" alignItems="center">
                      <LanguageIcon fontSize="small" sx={{ mr: 0.5 }} />
                      Language
                    </Box>
                  }
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: { maxHeight: 200, mt: 1 },
                      },
                      MenuListProps: {
                        dense: true,
                        sx: {
                          py: 0,
                          "& .MuiMenuItem-root": {
                            fontSize: "0.85rem",
                            minHeight: "30px",
                            px: 1.5,
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="en_US">English-US</MenuItem>
                  <MenuItem value="en_GB">English-UK</MenuItem>
                  <MenuItem value="en_IN">English-IND</MenuItem>
                  <MenuItem value="hi">Hindi</MenuItem>
                  <MenuItem value="bn_IN">Bengali-IND</MenuItem>
                  <MenuItem value="bn">Bengali</MenuItem>
                  <MenuItem value="gu">Gujarati</MenuItem>
                  <MenuItem value="mr">Marathi</MenuItem>
                  <MenuItem value="ta">Tamil</MenuItem>
                  <MenuItem value="te">Telugu</MenuItem>
                  <MenuItem value="kn">Kannada</MenuItem>
                  <MenuItem value="ml">Malayalam</MenuItem>
                  <MenuItem value="pa">Punjabi</MenuItem>
                  <MenuItem value="or">Odia</MenuItem>
                </TextField>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel>Template Type</InputLabel>
                <Select
                  value={templateType}
                  label="Template Type"
                  onChange={(e) => setTemplateType(e.target.value)}
                >
                  <MenuItem value="Marketing">Marketing</MenuItem>
                  <MenuItem value="Utility">Utility</MenuItem>
                </Select>
              </FormControl>

              <Divider>Header</Divider>
              <FormControl size="small" fullWidth>
                <InputLabel>
                  <TextFieldsIcon fontSize="small" /> Header Type
                </InputLabel>
                <Select
                  value={headerType}
                  label="Header Type"
                  onChange={(e) => setHeaderType(e.target.value)}
                >
                  <MenuItem value="NONE">None</MenuItem>
                  <MenuItem value="TEXT">Text</MenuItem>
                  <MenuItem value="IMAGE">Image</MenuItem>
                  <MenuItem value="VIDEO">Video</MenuItem>
                  <MenuItem value="DOCUMENT">Document</MenuItem>
                </Select>
              </FormControl>

              {headerType === "TEXT" && (
                <>
                  <TextField
                    size="small"
                    label="Header Text (use {{1}})"
                    helperText="Use {{1}} only if variable is needed"
                    value={headerText}
                    onChange={(e) => setHeaderText(e.target.value)}
                    fullWidth
                  />
                  {placeholders.header.includes("1") && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      {/* <FormControl size="small">
                        <Select
                          value={variableTypes["1"]}
                          onChange={(e) =>
                            handleTypeChange("1", e.target.value)
                          }
                        >
                          <MenuItem value="Text">Text</MenuItem>
                          <MenuItem value="Number">Number</MenuItem>
                        </Select>
                      </FormControl> */}
                      <TextField
                        size="small"
                        label="Example for {{1}}"
                        value={headerExamples["1"]}
                        onChange={(e) =>
                          handleExampleChange("1", e.target.value, "header")
                        }
                        fullWidth
                      />
                    </Stack>
                  )}
                </>
              )}

              {["IMAGE", "VIDEO", "DOCUMENT"].includes(headerType) && (
                <TextField
                  type="file"
                  size="small"
                  onChange={handleFileChange}
                  fullWidth
                />
              )}

              <Divider>Body</Divider>
              <TextField
                size="small"
                label="Body Text (use {{1}}, {{2}},...)"
                helperText="Use {{n}} for each dynamic value"
                multiline
                rows={3}
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                fullWidth
              />
              {placeholders.body.map((id) => (
                <Stack key={id} direction="row" spacing={1} alignItems="center">
                  {/* <FormControl size="small">
                    <Select
                      value={variableTypes[id]}
                      onChange={(e) => handleTypeChange(id, e.target.value)}
                    >
                      <MenuItem value="Text">Text</MenuItem>
                      <MenuItem value="Number">Number</MenuItem>
                    </Select>
                  </FormControl> */}
                  <TextField
                    size="small"
                    label={`Example for {{${id}}}`}
                    value={bodyExamples[id]}
                    onChange={(e) => handleExampleChange(id, e.target.value)}
                    fullWidth
                  />
                </Stack>
              ))}

              <Divider>Footer</Divider>
              <TextField
                size="small"
                label="Footer Text"
                value={footerText}
                onChange={(e) => setFooterText(e.target.value)}
                fullWidth
              />

              <Divider>Buttons</Divider>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  label="Button Text"
                  value={buttonText}
                  onChange={(e) => setButtonText(e.target.value)}
                />
                <IconButton onClick={addButton}>
                  <AddCircleOutlineIcon />
                </IconButton>
              </Stack>
              <Box>
                {buttons.map((b, i) => (
                  <Chip
                    key={i}
                    label={b}
                    onDelete={() => removeButton(b)}
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
              </Box>

              <Button
                type="submit"
                variant="contained"
                startIcon={<MessageIcon />}
                fullWidth
              >
                Submit Template
              </Button>
            </Stack>
          </Grid>

          {/* Preview */}
          <Grid item xs={12} md={5} sx={{ width: 350 }}>
            <Fade in>
              <Paper sx={{ p: 2, bgcolor: "#f4fafe", borderRadius: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  <PreviewIcon sx={{ mr: 1 }} /> Template Preview
                </Typography>

                {headerType === "TEXT" && headerText && (
                  <Typography fontWeight="bold">
                    {headerText.replace(
                      /{{(\d+)}}/g,
                      (_, id) => headerExamples[id] || `{{${id}}}`
                    )}
                  </Typography>
                )}
                {headerType === "IMAGE" && mediaFile && (
                  <Box
                    component="img"
                    src={URL.createObjectURL(mediaFile)}
                    sx={{
                      width: "100%",
                      maxHeight: 140,
                      objectFit: "cover",
                      mb: 1,
                    }}
                  />
                )}
                {headerType === "VIDEO" && mediaFile && (
                  <Box
                    component="video"
                    src={URL.createObjectURL(mediaFile)}
                    controls
                    sx={{ width: "100%", maxHeight: 140, mb: 1 }}
                  />
                )}
                {headerType === "DOCUMENT" && mediaFile && (
                  <Typography sx={{ mb: 1 }}>
                    Uploaded: {mediaFile.name}
                  </Typography>
                )}

                <Typography sx={{ mb: 1 }}>
                  {bodyText.replace(
                    /{{(\d+)}}/g,
                    (_, id) => bodyExamples[id] || `{{${id}}}`
                  )}
                </Typography>
                {footerText && (
                  <Typography variant="caption" color="text.secondary">
                    {footerText}
                  </Typography>
                )}
                <Box sx={{ mt: 1 }}>
                  {buttons.map((b, i) => (
                    <Button
                      key={i}
                      variant="outlined"
                      size="small"
                      sx={{ mr: 1, mb: 1 }}
                    >
                      {b}
                    </Button>
                  ))}
                </Box>
              </Paper>
            </Fade>
          </Grid>
        </Grid>
      </Box>
    </Grid>
  );
};

export default function WrappedCreateTemplate() {
  return (
    <>
      <CreateTemplate />
      <ToastContainer autoClose={3000} position="top-right" />
    </>
  );
}
